import { z } from 'zod';
import { agentChatModelSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';
import { promptDetailsSchema, promptKindSchema, promptListItemSchema, promptMessageSchema } from '../types/prompts.js';

export const getPromptsResponseSchema = z.object({
  items: z.array(promptListItemSchema),
});

export const createPromptRequestBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().default(''),
  messages: z.array(promptMessageSchema).default([]),
  inputSchema: z.record(z.string(), z.unknown()).default({}),
  llmModel: agentChatModelSchema.default('gpt-4.1-mini'),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const createPromptResponseSchema = z.object({
  id: z.number(),
  identifier: z.uuid(),
});

export const getPromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

export const getPromptResponseSchema = promptDetailsSchema;

export const updatePromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

export const updatePromptRequestBodySchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  status: StatusTypeSchema,
  messages: z.array(promptMessageSchema),
  inputSchema: z.record(z.string(), z.unknown()),
  llmModel: agentChatModelSchema,
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const deletePromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

// --- Prompt Versions ---

export const promptVersionListItemSchema = z.object({
  id: z.number(),
  version: z.number(),
  name: z.string(),
  description: z.string(),
  kind: promptKindSchema,
  llmModel: agentChatModelSchema,
  messages: z.array(promptMessageSchema),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
  status: StatusTypeSchema,
  savedOn: z.coerce.date(),
});
export type PromptVersionListItem = z.infer<typeof promptVersionListItemSchema>;

export const getPromptVersionsResponseSchema = z.object({
  promptIdentifier: z.uuid(),
  promptCreatedOn: z.coerce.date(),
  versions: z.array(promptVersionListItemSchema),
});
export type GetPromptVersionsResponse = z.infer<typeof getPromptVersionsResponseSchema>;

export const recordQuerySubmissionRequestParamsSchema = z.object({
  promptIdentifier: z.uuid(),
});

export const recordQuerySubmissionRequestBodySchema = z.object({
  referenceId: z.string().optional(),
});

export const recordQuerySubmissionResponseSchema = z.object({
  success: z.literal(true),
});
export namespace UpdatePromptStatus {
  export const paramsSchema = z.object({
    promptId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    status: StatusTypeSchema,
  });
  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = promptDetailsSchema;
  export type Response = z.infer<typeof responseSchema>;
}

// --- Query Log ---

export const queryLogListItemSchema = z.object({
  queryIdentifier: z.string(),
  queryText: z.string(),
  promptId: z.number().nullable(),
  promptName: z.string().nullable(),
  queryType: z.enum(['instruction', 'user']),
  userEmail: z.string().nullable(),
  appName: z.string().nullable(),
  brandIdentifier: z.string().nullable(),
  status: z.enum(['success', 'error']),
  tokenCount: z.number().nullish(),
  inputTokenCount: z.number().nullish(),
  outputTokenCount: z.number().nullish(),
  durationMs: z.number().nullish(),
  createdOn: z.coerce.date(),
});
export type QueryLogListItem = z.infer<typeof queryLogListItemSchema>;

export const queryLogDetailSchema = queryLogListItemSchema.extend({
  model: z.string().nullable(),
  promptMessages: z.array(promptMessageSchema).nullable(),
  response: z.unknown().nullable(),
});
export type QueryLogDetail = z.infer<typeof queryLogDetailSchema>;

export const queryLogStatsSchema = z.object({
  total: z.number(),
  successCount: z.number(),
  instructionCount: z.number(),
  userCount: z.number(),
});
export type QueryLogStats = z.infer<typeof queryLogStatsSchema>;

export const getQueryLogRequestQuerySchema = z.object({
  promptId: z.coerce.number().optional(),
  userEmail: z.string().optional(),
  appName: z.string().optional(),
  brandIdentifier: z.string().optional(),
  status: z.enum(['success', 'error']).optional(),
  search: z.string().optional(),
  take: z.coerce.number().int().min(0).max(500).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});
export type GetQueryLogRequestQuery = z.infer<typeof getQueryLogRequestQuerySchema>;

export const getQueryLogResponseSchema = z.object({
  items: z.array(queryLogListItemSchema),
  total: z.number(),
  stats: queryLogStatsSchema,
});

export const getQueryLogDetailResponseSchema = z.object({
  item: queryLogDetailSchema,
});
